import { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from "@react-google-maps/api";
import { API, useAuth , authFetch } from "@/App";
import {
  MapPin, Store, Search, Ruler, Navigation, X, LocateFixed, Building2
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AVI_LOCATIONS } from "@/lib/constants";

const mapStyle = { width: "100%", height: "100%", borderRadius: "0 0 12px 12px" };
const TYPE_COLORS = { DEALER: "#3B82F6", DISTRIBUTOR: "#F59E0B", CONTRACTOR: "#10B981", BUILDER: "#8B5CF6", END_USER: "#6B7280" };

const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const DealerMapPage = () => {
  const { user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(user?.region_id || "ALL");
  const [search, setSearch] = useState("");
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [distanceMode, setDistanceMode] = useState(false);
  const [distancePoints, setDistancePoints] = useState([]);
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  useEffect(() => {
    const fetchDealers = async () => {
      setLoading(true);
      try {
        const params = region !== "ALL" ? `?region=${region}` : "";
        const res = await authFetch(`${API}/dealers/map${params}`, {  });
        const json = await res.json();
        if (json.success) setDealers(json.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchDealers();
  }, [region]);

  const filtered = dealers.filter(d =>
    !search || d.firm_name?.toLowerCase().includes(search.toLowerCase()) || d.district?.toLowerCase().includes(search.toLowerCase())
  );

  const center = filtered.length > 0
    ? { lat: filtered.reduce((s, d) => s + d.lat, 0) / filtered.length, lng: filtered.reduce((s, d) => s + d.lng, 0) / filtered.length }
    : { lat: 23.0, lng: 72.6 };

  const handleMapClick = useCallback((e) => {
    if (!distanceMode) return;
    const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setDistancePoints(prev => [...prev, pt]);
  }, [distanceMode]);

  const totalDistance = distancePoints.length > 1
    ? distancePoints.reduce((sum, pt, i) => i === 0 ? 0 : sum + haversine(distancePoints[i - 1].lat, distancePoints[i - 1].lng, pt.lat, pt.lng), 0)
    : 0;

  const showMap = isLoaded && !loadError && !mapError;

  return (
    <div className="space-y-4" data-testid="dealer-map-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Dealer Map</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} dealers on map · Distance measurement tool</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Search dealer..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 w-[200px]" data-testid="dealer-search" />
          </div>
          {user?.role === "SUPER_ADMIN" && (
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="w-[120px] h-9" data-testid="region-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Regions</SelectItem>
                <SelectItem value="GJ">Gujarat</SelectItem>
                <SelectItem value="RJ">Rajasthan</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button
            variant={distanceMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setDistanceMode(!distanceMode); setDistancePoints([]); }}
            className={distanceMode ? "bg-[#8B1A1A] hover:bg-[#6B1515]" : ""}
            data-testid="distance-tool-btn">
            <Ruler className="w-4 h-4 mr-1" /> {distanceMode ? "Exit Distance" : "Measure Distance"}
          </Button>
        </div>
      </div>

      {/* Distance measurement display */}
      {distanceMode && (
        <Card className="px-4 py-2.5 border-[#8B1A1A] bg-red-50/30" data-testid="distance-display">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <Ruler className="w-4 h-4 text-[#8B1A1A]" />
              <span className="text-slate-600">Click on map to add points. </span>
              <span className="font-mono font-bold text-slate-900">{distancePoints.length} points</span>
              {totalDistance > 0 && (
                <span className="font-mono font-bold text-[#8B1A1A]">{totalDistance.toFixed(1)} km</span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setDistancePoints([])} className="text-xs text-slate-500" data-testid="clear-distance">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        </Card>
      )}

      {/* Map + List */}
      <div className="grid grid-cols-12 gap-4" style={{ height: "calc(100vh - 240px)" }}>
        {/* Dealer list */}
        <div className="col-span-3 bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" data-testid="dealer-list-panel">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{filtered.length} Dealers</p>
          </div>
          {/* Map Legend */}
          <div className="px-3 py-2 border-b border-slate-100 space-y-1">
            <p className="text-[9px] font-semibold text-slate-400 uppercase">Legend</p>
            <div className="flex flex-wrap gap-2">
              {AVI_LOCATIONS.map(l => (
                <div key={l.id} className="flex items-center gap-1" title={l.label}>
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
                  <span className="text-[9px] text-slate-500">{l.icon}</span>
                </div>
              ))}
              {Object.entries(TYPE_COLORS).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-[9px] text-slate-500">{type}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.map(d => (
              <button key={d.party_id} onClick={() => {
                setSelectedDealer(d.party_id);
                if (mapRef.current) { mapRef.current.panTo({ lat: d.lat, lng: d.lng }); mapRef.current.setZoom(14); }
              }}
                className={`w-full text-left px-3 py-2 border-b border-slate-50 hover:bg-slate-50 transition-colors ${selectedDealer === d.party_id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}
                data-testid={`dealer-item-${d.party_id}`}>
                <p className="text-xs font-semibold text-slate-800 truncate">{d.firm_name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Badge variant="outline" className="text-[8px] px-1 py-0">{d.party_type}</Badge>
                  <span className="text-[9px] text-slate-400">{d.district}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Map */}
        <div className="col-span-9 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="dealer-map-container">
          {showMap ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%", borderRadius: "12px" }}
              center={center} zoom={8}
              onLoad={map => { mapRef.current = map; }}
              onClick={handleMapClick}
              options={{
                styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
                mapTypeControl: true, streetViewControl: true, fullscreenControl: true,
                ...(distanceMode ? { draggableCursor: "crosshair" } : {}),
              }}>
              {/* Dealer markers */}
              {filtered.map(d => (
                <Marker key={d.party_id}
                  position={{ lat: d.lat, lng: d.lng }}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW || 3,
                    scale: 6, fillColor: TYPE_COLORS[d.party_type] || "#6B7280",
                    fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
                  }}
                  title={d.firm_name}
                  zIndex={selectedDealer === d.party_id ? 10 : 1}
                  onClick={() => setSelectedDealer(selectedDealer === d.party_id ? null : d.party_id)}>
                  {selectedDealer === d.party_id && (
                    <InfoWindow onCloseClick={() => setSelectedDealer(null)}>
                      <div className="text-xs p-1 max-w-[220px]">
                        <p className="font-semibold text-slate-900">{d.firm_name}</p>
                        <p className="text-slate-500 mt-0.5">{d.contact_person} · {d.mobile_number}</p>
                        <p className="text-slate-400 mt-0.5">{d.address || d.district}</p>
                        {d.outstanding > 0 && <p className="text-red-600 font-mono mt-1">Outstanding: {d.outstanding.toLocaleString()}</p>}
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${d.lat},${d.lng}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#8B1A1A", fontWeight: 600, marginTop: "6px", fontSize: "11px" }}>
                          Get Directions
                        </a>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
              {/* Distance measurement line */}
              {distancePoints.length > 1 && (
                <Polyline path={distancePoints} options={{ strokeColor: "#8B1A1A", strokeWeight: 3, strokeOpacity: 0.8 }} />
              )}
              {/* Distance markers */}
              {distancePoints.map((pt, i) => (
                <Marker key={`dist-${i}`} position={pt}
                  icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 6, fillColor: "#8B1A1A", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                  title={`Point ${i + 1}`} />
              ))}

              {/* HQ and Plant Markers */}
              {AVI_LOCATIONS.map(loc => (
                <Marker key={loc.id}
                  position={{ lat: loc.lat, lng: loc.lng }}
                  icon={{
                    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
                    scale: 2.2, fillColor: loc.color, fillOpacity: 1,
                    strokeColor: "#fff", strokeWeight: 2,
                    anchor: window.google ? new window.google.maps.Point(12, 24) : undefined,
                  }}
                  title={loc.label}
                  zIndex={100}>
                  {selectedDealer === loc.id && (
                    <InfoWindow onCloseClick={() => setSelectedDealer(null)}>
                      <div className="text-xs p-1 max-w-[220px]">
                        <p className="font-bold text-slate-900">{loc.icon} {loc.label}</p>
                        <p className="text-slate-500 mt-1">{loc.address}</p>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400">
              <MapPin className="w-8 h-8 mr-2" /> Map loading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealerMapPage;
